// React imports
import { useEffect, useRef, useState } from 'react';
import '../../../src/App';
import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Skeleton,
  Table,
  Tooltip,
  Typography,
} from 'antd';

const { Title } = Typography;

// Third-party imports
import { useAuth } from 'react-oidc-context';
import { useDispatch } from 'react-redux';

import { ScrollableContent } from 'src/components/ScrollableContent';

import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import { formatDate } from 'src/utils/helpers';
import {
  approveReporteesTimesheet,
  getAndViewTimesheetByUser,
  getUserDataByUser,
} from 'src/redux/features/timesheet/slice';
import ViewTimesheet from './ViewTimesheet';
import { toast } from 'react-toastify';
import { TimesheetStatusMap } from 'src/models/Timesheet';
import SharedDatePicker from 'src/components/DatePicker';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  HourglassOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Card, Input, Switch } from 'antd';
import AntDButton from 'src/components/Button';
import en from 'src/messages/en.json';
import SearchableDropdown from 'src/components/SearchableDropdown';
import { setError } from 'src/redux/features/common/commonSlice';

const BulkApprovals = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeRequest = useRef<any>(null);

  const [weekDate, setWeekDate] = useState<string>(formatDate(new Date()));
  const [value, setValue] = useState('');
  const [resourceName, setResourceName] = useState('');
  const [UserData, setUserData] = useState([]);
  const [filteredProjectList, setFilteredProjectList] = useState([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [checkedEmails, setCheckedEmails] = useState<string[]>([]);

  const [openModal, setOpenModal] = useState(false);
  const [resourceData, setResourceData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingViewTimesheet, setIsLoadingViewTimeshee] = useState(false);
  const timesheetStatus = ['Unknown', 'Approved', 'Rejected'];
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [showDirectOnly, setShowDirectOnly] = useState(true);
  const [reporteeScope, setReporteeScope] = useState('direct'); // 'all' or 'direct'

  const onWeekChange = async (date) => {
    setIsLoading(true);
    setWeekDate(date);
    try {
      const response = await dispatch(
        getUserDataByUser({
          resource: auth?.user?.profile?.email,
          date: formatDate(date),
          showActiveOnly,
          showDirectOnly,
        }),
      );

      setUserData(response.payload);
      setIsLoading(false);

      return;
    } catch (error) {
      console.error('Error fetching timesheet:', error);
    }
  };

  useEffect(() => {
    if (auth?.user?.profile?.email) {
      setIsLoading(true);
      const promise = dispatch(
        getUserDataByUser({
          resource: auth.user.profile.email,
          date: formatDate(weekDate),
          showActiveOnly,
          showDirectOnly,
        }),
      );

      activeRequest.current = promise;

      promise.then((response) => {
        if (activeRequest.current === promise) {
          setUserData(response.payload);
          setIsLoading(false);
        }
      });
    }
  }, [auth?.user?.profile?.email, showActiveOnly, showDirectOnly]);

  const handleApproveReporteesTimesheetConfirm = async () => {
    setIsSaving(true);

    const requestBody = {
      approverEmail: auth?.user?.profile?.email,
      status: 2,
      date: formatDate(weekDate),
      resources: checkedEmails,
      showActiveOnly,
      showDirectOnly,
    };

    try {
      const response = await dispatch(
        approveReporteesTimesheet(requestBody),
      ).unwrap();

      setCheckedEmails([]);
      setUserData(response);

      toast.success(en.Timesheet.Success.resourceTimesheetApproved);
    } catch (error) {
      dispatch(setError(en.Timesheet.Error.approveTimesheetFailed));
    } finally {
      setIsSaving(false);
    }
  };

  const onViewTimesheet = async (userEmail: string, userName: string) => {
    setResourceName(userName);
    setIsLoadingViewTimeshee(true);
    setOpenModal(true);
    try {
      const response = await dispatch(
        getAndViewTimesheetByUser({
          resource: userEmail,
          date: dayjs(weekDate || new Date()).format('YYYY-MM-DD'),
        }),
      );

      setResourceData(response.payload);
      setIsLoadingViewTimeshee(false);
      setIsLoading(false);

      return;
    } catch (error) {
      console.error('Error fetching timesheet:', error);
    }
  };
  const closeModal = () => {
    setOpenModal(false);
    navigate('/bulk-approvals');
  };

  const handleCheckboxChange = (email: string) => {
    setCheckedEmails((prevEmails) => {
      const isChecked = prevEmails.includes(email);

      return isChecked
        ? prevEmails.filter((e) => e !== email)
        : [...prevEmails, email];
    });
  };
  const handleReporteeScopeChange = (value) => {
    setReporteeScope(value);
    setShowDirectOnly(!showDirectOnly);
  };

  const toggleExpand = (path, expandedRows, setExpandedRows) => {
    const newExpanded = new Set(expandedRows);

    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedRows(newExpanded);
  };
  const flattenData = (nodes, expandedSet, level = 0, parentPath = '') => {
    return nodes.reduce((acc, node, index) => {
      const path = `${parentPath}${index}`;
      const isExpanded = expandedSet.has(path);
      const children =
        isExpanded && node.reportees?.length
          ? flattenData(node.reportees, expandedSet, level + 1, `${path}-`)
          : [];

      acc.push({ ...node, level, path, isExpanded });

      return acc.concat(children);
    }, []);
  };

  const flatData = flattenData(filteredProjectList || [], expandedRows);

  const columns = [
    {
      title: en.Timesheet.Title.users,
      dataIndex: 'name',
      key: 'name',
      width: '40%',
      render: (text, record, index) => {
        const disabledCheckbox = timesheetStatus.includes(
          TimesheetStatusMap[record.status],
        );

        return (
          <Flex gap={'middle'} align="center">
            <div style={{ width: '20px', flexShrink: 0 }}>
              <Checkbox
                checked={checkedEmails.includes(record.email)}
                disabled={disabledCheckbox}
                onChange={() => handleCheckboxChange(record.email)}
                className={disabledCheckbox ? 'custom-disabled-checkbox' : ''}
              />
            </div>

            <Flex
              align="center"
              style={{ marginLeft: `${record.level * 20}px` }}
            >
              {record.reportees?.length > 0 && (
                <Button
                  type="link"
                  style={{ padding: 0, marginRight: 8 }}
                  onClick={() =>
                    toggleExpand(record.path, expandedRows, setExpandedRows)
                  }
                >
                  {record.isExpanded ? (
                    <MinusSquareOutlined
                      style={{ fontSize: '17px', color: 'rgba(0, 0, 0, 0.85)' }}
                    />
                  ) : (
                    <PlusSquareOutlined
                      style={{ fontSize: '17px', color: 'rgba(0, 0, 0, 0.85)' }}
                    />
                  )}
                </Button>
              )}
              <Typography
                style={{
                  marginLeft: record.reportees?.length > 0 ? '0' : '28px',
                }}
              >
                {record.name}
                {!record.isActive && (
                  <span style={{ marginLeft: '4px' }}>*</span>
                )}
              </Typography>
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: en.Timesheet.Title.submittedHours,
      dataIndex: 'duration',
      key: 'duration',
      width: '25%',
      render: (duration, record) => (
        <>
          {duration} Hours &nbsp;
          <Tooltip
            placement="bottom"
            title={
              TimesheetStatusMap[record.status] === 'Unknown'
                ? 'Not Submitted'
                : TimesheetStatusMap[record.status]
            }
          >
            {TimesheetStatusMap[record.status] === 'Rejected' ? (
              <CloseCircleOutlined style={{ color: '#FF0000' }} />
            ) : TimesheetStatusMap[record.status] === 'Approved' ? (
              <CheckCircleOutlined style={{ color: '#56DA26' }} />
            ) : TimesheetStatusMap[record.status].includes('Unknown') ||
              TimesheetStatusMap[record.status].includes('Saved') ? (
              <HourglassOutlined />
            ) : (
              ''
            )}
          </Tooltip>
        </>
      ),
    },
    {
      title: en.Common.Title.actions,
      key: 'actions',
      width: '15%',
      render: (_, record) => (
        <Button
          type="link"
          style={{ textDecoration: 'underline' }}
          onClick={() => {
            onViewTimesheet(record.email, record.name);
            window.history.pushState(
              {},
              '',
              `/bulk-approvals/${formatDate(weekDate)}/${record.email}`,
            );
          }}
          disabled={record.duration === 0}
        >
          View Timesheet
        </Button>
      ),
    },
  ];

  useEffect(() => {
    setCheckedEmails([]);
  }, [weekDate]);

  useEffect(() => {
    const searchUser = (users: any[], query: string): any[] => {
      if (!query) return users;

      query = query.toLowerCase();

      const filterUsers = (userList: any[]): any[] => {
        return userList
          .map((user) => {
            if (!user.name) return null;

            const isMatch = user.name.toLowerCase().includes(query);

            const filteredReportees = user.reportees?.length
              ? filterUsers(user.reportees)
              : [];

            if (isMatch || filteredReportees.length > 0) {
              return { ...user, reportees: filteredReportees };
            }

            return null;
          })
          .filter(Boolean);
      };

      return filterUsers(users);
    };

    setFilteredProjectList(searchUser(UserData, value));
  }, [value, UserData]);

  return (
    <>
      <Title level={4} style={{ textAlign: 'left' }}>
        {en.Timesheet.Title.bulkApprovals}
      </Title>
      <Flex gap="small" vertical>
        <Card size="small">
          <Flex justify="space-between" align="center">
            <Flex gap="middle" align="center">
              <SharedDatePicker
                date={new Date(weekDate)}
                onChange={onWeekChange}
                mode="week"
                placeholder={en.Timesheet.Placeholder.selectWeek}
                size="middle"
                showWeek={false}
              />

              <Flex gap="small" align="center">
                <Switch
                  checked={showActiveOnly}
                  onChange={() => setShowActiveOnly(!showActiveOnly)}
                />
                <Typography.Text strong>Active Users</Typography.Text>
              </Flex>
            </Flex>

            <Flex gap="middle" align="center">
              <SearchableDropdown
                selected={[reporteeScope]}
                onChange={handleReporteeScopeChange}
                style={{ width: 160 }}
                options={[
                  { label: 'All Reportees', value: 'all' },
                  { label: 'Direct Reportees', value: 'direct' },
                ]}
                showSearch={false}
                allowClear={false}
                showArrow={true}
              />

              <Input
                placeholder={en.Common.Placeholder.search}
                size="middle"
                value={value}
                prefix={<SearchOutlined />}
                onChange={(e) => setValue(e.target.value)}
                style={{ width: 260 }}
                allowClear
              />
            </Flex>
          </Flex>
        </Card>

        <Card size="small">
          <ScrollableContent
            minHeight="calc(100vh - 250px)"
            maxHeight="calc(100vh - 250px)"
          >
            <Table
              dataSource={flatData}
              loading={isLoading}
              columns={columns}
              rowKey="email"
              sticky
              expandable={{
                rowExpandable: (record) => !!record.children?.length,
              }}
              className="custom-antd-po-table"
              pagination={false}
              bordered={false}
              size="small"
            />
          </ScrollableContent>
          <Flex
            gap={'small'}
            style={{ borderTop: '1px solid #e0e0e0', paddingTop: '12px' }}
          >
            <Flex align="center" style={{ paddingRight: '16px' }} gap="small">
              <HourglassOutlined />
              <Typography.Text>{en.Timesheet.Label.pending}</Typography.Text>
            </Flex>

            <Flex align="center" gap="small" style={{ paddingRight: '16px' }}>
              <CheckCircleOutlined style={{ color: '#56DA26' }} />
              <Typography.Text>{en.Timesheet.Label.approved}</Typography.Text>
            </Flex>

            <Flex align="center" gap="small">
              <CloseCircleOutlined style={{ color: '#FF0000' }} />
              <Typography.Text>{en.Timesheet.Label.rejected}</Typography.Text>
            </Flex>
            <Flex style={{ marginLeft: 'auto' }}>
              <AntDButton
                type="primary"
                onClick={handleApproveReporteesTimesheetConfirm}
                disabled={!checkedEmails.length}
                status="Approving..."
                statusOnComplete="Approve"
                htmlType="button"
                loading={isSaving}
              />
            </Flex>
          </Flex>

          <ViewTimesheet
            show={openModal}
            onHide={closeModal}
            timesheetData={resourceData}
            resourceName={resourceName}
            isLoading={isLoadingViewTimesheet}
          />
        </Card>
      </Flex>
    </>
  );
};

export default BulkApprovals;
